import React from 'react';
import ReactPlayer from 'react-player';
import moment from 'moment';
import FacebookPlayer from 'react-facebook-player';

import { YOUTUBE_WATCH_URL, YOUTUBE, INSTAGRAM, FACEBOOK, SNAPCHAT, TWITTER } from '../../utils/static-configuration';
import { roundNumberToSmallestDecimal, round } from '../../utils/helper-functions';
import { scientificFormat, formatNumberWithPercentage } from '../../utils/helpers/formatting';
import AnalyticsTooltip from '../../components/analytics-tooltip';

const renderVideo = (videoUrl, wrapperStyle) => {
  if (!videoUrl) return null;
  return (
    <div className={wrapperStyle}>
      <ReactPlayer url={videoUrl} width={'100%'} height={'100%'} controls />
    </div>
  );
};

const renderFacebookVideo = videoId => (
  <div className="facebook-wrapper">
    <FacebookPlayer
      videoId={videoId}
      appId={process.env.REACT_APP_FACEBOOK_APP_ID}
      width={400}
    />
  </div>
  );

const renderImage = thumbnail => {
  if (!thumbnail) return null;
  return (
    <div className="instagram-wrapper">
      <img src={thumbnail} />
    </div>
  );
};

const YOUTUBE_COMPLIANT = false;

const renderTooltip = (description, title, value, denominator) => (
  <AnalyticsTooltip content={description} style="analytics-frame">
    { renderStatsItem(title, value, denominator) }
  </AnalyticsTooltip>
  );

const renderStatsItem = (title, value, denominator) => (
  <div className="stats">
    <p className="bold">{ title }</p>
    <div className="spacer small" />
    <h4>{ scientificFormat(value) }</h4>
    { (YOUTUBE_COMPLIANT && value > 0 && denominator > 0) ? <p className="light">{ formatNumberWithPercentage(value, denominator) }</p> : null }
  </div>
  );

const AnalyticsVideoInfoCard = ({ trackedAsset, budget, onBudgetChange, openEngagementScoreModal }) => {
  if (!trackedAsset || !trackedAsset.assetId) return null;

  const denominator = trackedAsset.isVideo ? { value: trackedAsset.views, name: 'views' } : { value: trackedAsset.followers, name: 'followers' };
  const platform = trackedAsset.platform;

  const youtubeInfo = {
    asset: renderVideo(trackedAsset.videoUrl, 'asset-wrapper'),
    first: renderTooltip('Number of likes as a percentage of views.', 'likes', trackedAsset.likes, denominator.value),
    second: renderTooltip('Number of dislikes as a percentage of views.', 'dislikes', trackedAsset.dislikes, denominator.value),
    third: renderTooltip('Number of comment as a percentage of views.', 'comments', trackedAsset.comments, denominator.value),
    fourth: renderStatsItem('dyzio score', trackedAsset.engagementScore),
    fifth: renderStatsItem(denominator.name, denominator.value),
    showCostPerView: true,
  };

  const instagramInfo = {
    asset: trackedAsset.isVideo ? renderVideo(trackedAsset.videoUrl) : renderImage(trackedAsset.thumbnail, 'instagram-wrapper'),
    first: renderTooltip('Number of likes as a percentage of views.', 'likes', trackedAsset.likes, denominator.value),
    second: renderTooltip('Number of comment as a percentage of views.', 'comments', trackedAsset.comments, denominator.value),
    third: renderStatsItem('engagement', trackedAsset.engagementScore),
    fourth: trackedAsset.isVideo ? renderStatsItem(denominator.name, denominator.value) : null,
    fifth: renderStatsItem('followers', trackedAsset.followers),
    showCostPerView: trackedAsset.isVideo,
  };

  const facebookInfo = {
    asset: trackedAsset.isVideo ? renderFacebookVideo(trackedAsset.assetId) : renderImage(trackedAsset.thumbnail, 'instagram-wrapper'),
    first: renderStatsItem('shares', trackedAsset.shares),
    second: renderStatsItem('likes', trackedAsset.likes),
    third: renderStatsItem('reactions', trackedAsset.reactions),
    fourth: renderStatsItem('comments', trackedAsset.comments),
    fifth: renderStatsItem('views', trackedAsset.views),
  };

  const infoOptions = {
    YOUTUBE: youtubeInfo,
    INSTAGRAM: instagramInfo,
    FACEBOOK: facebookInfo,
  };

  const analyticsInfo = infoOptions[platform];

  const wordBreak = { wordBreak: 'break-word' };

  return (
    <div className="info-card">
      <div className="info-left">
        { analyticsInfo.asset }
      </div>
      <div className="info-right">
        <div className="column-item">
          { trackedAsset.publishedDate &&
            <span>
              <p className="bold">Live date: </p>
              <p>{ moment(trackedAsset.publishedDate).format('Do MMM') }</p>
            </span>
          }
        </div>
        <div className="column-item">
          { trackedAsset.copy &&
            <span>
              <div className="spacer small" />
              <p className="bold">Copy: </p>
              <p style={wordBreak}>{ trackedAsset.copy }</p>
            </span>
          }
        </div>
        <div className="column-item">
          { analyticsInfo.first &&
            <div className="video-card">
              { analyticsInfo.first && <div className="flex-item">{ analyticsInfo.first }</div> }
              { analyticsInfo.second && <div className="flex-item">{ analyticsInfo.second }</div> }
              { analyticsInfo.third && <div className="flex-item">{ analyticsInfo.third }</div> }
              { analyticsInfo.fourth && <div className="flex-item">{ analyticsInfo.fourth }</div> }
              { analyticsInfo.fifth && <div className="flex-item">{ analyticsInfo.fifth }</div> }
              { analyticsInfo.sixth && <div className="flex-item">{ analyticsInfo.sixth }</div> }
            </div>
          }
        </div>
        { analyticsInfo.showCostPerView &&
          <div className="column-item">
            <div className="calculator">
              <h4>Cost per view?</h4>
              <p className="small">Add the budget for this video to get the cost per view</p>
              <div className="spacer" />
              <div className="calculator-input">
                <input name="budget" id="budget" type="number" value={budget} onChange={onBudgetChange} placeholder="0.00" step="any" min="0" />
                <label htmlFor="budget"><span className="small">£</span></label>
                { trackedAsset && budget &&
                  <p className="result">
                    £{ roundNumberToSmallestDecimal((budget / trackedAsset.views), 0) }
                  </p>
                }
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  );
};

AnalyticsVideoInfoCard.propTypes = {
  onBudgetChange: React.PropTypes.func.isRequired,
  openEngagementScoreModal: React.PropTypes.func,
};

export default AnalyticsVideoInfoCard;



/** WEBPACK FOOTER **
 ** ./src/components/analytics-video-info-card/index.js
 **/